import { inject, Injectable } from '@angular/core';
import { ApiService } from '../../../../core/services/ApiService';
import { map, Observable } from 'rxjs';
import { IResponse } from '../../../../core/interfaces/IResponse';
import { ICustomer } from '../interfaces/ICustomer';
import { CancelPreviousApiCall } from '../../../../core/decorators/cancel-previous-api-call.decorator';
import { IActionHistoryRecord } from '../interfaces/IActionHistoryRecord';
import { ICustomerCard } from '../interfaces/ICustomerCard';
import { ILog } from '../interfaces/ILog';
import { IPagination } from '../../../../core/interfaces/IPagination';
import { IPagedResults } from '../../../../core/interfaces/IPagedResults';
import { ICustomerTransactionPosition } from '../interfaces/ICustomerTransactionPosition';
import { NewsletterStatues } from '../enums/NewsletterStatues';

@Injectable({
  providedIn: 'root'
})
export class CustomersService {

  private readonly _basePath = '/customers';
  private readonly _apiService = inject(ApiService);

  @CancelPreviousApiCall()
  public getCustomers(search = ''): Observable<IResponse<ICustomer[]>> {
    return this._apiService.get(`${this._basePath}?search=${search}`);
  }

  public getCustomerById(id: number, withDetails = false): Observable<IResponse<ICustomer>> {
    return this._apiService.get(`${this._basePath}/${id}?detailed=${withDetails}`)
      .pipe(
        map((res: IResponse<ICustomer>) => {
          if (res.data.subscriptions) {
            res.data.subscriptions.forEach(s => {
              s.isActive = s.subscriberId !== -1;

              if (s.status === NewsletterStatues.PENDING) {
                s.status = NewsletterStatues.ACTIVE;
              }
            });
          }

          return res;
        })
      );
  }

  public createCustomer(customer: ICustomer): Observable<IResponse<number>> {
    return this._apiService.post(this._basePath, customer);
  }

  public updateCustomer(customer: ICustomer): Observable<IResponse<number>> {
    return this._apiService.put(`${this._basePath}/${customer.id}`, customer);
  }

  public getCustomerActions(customer: ICustomer): Observable<IResponse<IActionHistoryRecord[]>> {
    return this._apiService.get(`${this._basePath}/${customer.id}/actions`);
  }

  public getCustomerCards(customer: ICustomer): Observable<IResponse<ICustomerCard[]>> {
    return this._apiService.get(`${this._basePath}/${customer.id}/cards`);
  }

  public getCustomerLogs(customer: ICustomer): Observable<IResponse<ILog[]>> {
    return this._apiService.get(`${this._basePath}/${customer.id}/logs`);
  }

  public getCustomerTransactionPositions(
    customerId: number,
    pagination: IPagination
  ): Observable<IResponse<IPagedResults<ICustomerTransactionPosition>>> {
    return this._apiService.get(`${this._basePath}/${customerId}/transaction-positions?page=${pagination.page}`);
  }

  public subscribeToNewsletter(cinemaId: number, customerId: number): Observable<IResponse<void>> {
    return this._apiService.post(`${this._basePath}/${customerId}/subscribe`, { cinemaId });
  }

  public unsubscribeFromNewsletter(cinemaId: number, customerId: number): Observable<IResponse<void>> {
    return this._apiService.post(`${this._basePath}/${customerId}/unsubscribe`, { cinemaId });
  }

  public resetPassword(email: string): Observable<void> {
    return this._apiService.post(`${this._basePath}/reset-password`, { email });
  }
}
